/** @jsxImportSource @emotion/react */
import type { ApiScan } from '@kaiizen/kai-pro-api';

import { CommonActionButton } from '@kaiizen/kai-shiki/react';
import { ArtifactEmptyState } from './ArtifactEmptyState';
import { AudienceDocArtifactCard } from './AudienceDocArtifactCard';
import {
  EYEBROW_STYLE,
  PRIMARY_ACTION_BUTTON_STYLE,
  SECTION_COPY_STYLE,
  SECTION_DESCRIPTION_STYLE,
  SECTION_HEADER_ROW_STYLE,
  SECTION_STYLE,
  SECTION_TITLE_STYLE,
  THREE_UP_CARD_GRID_STYLE,
} from './FocusedArtifactPanelStyleLib';

/** Renders minted audience-doc copies and the create-copy call to action. */
export const AudienceDocCopiesSection = ({
  audienceDocs,
  canCreateAudienceDoc,
  canManageAudienceDocShare,
  featureEnabled,
  selectedAudienceDocId,
  scopeArtifactTitlesById,
  onOpenCreateModal,
  onSelectAudienceDoc,
  onOpenAudienceDoc,
}: {
  audienceDocs: ApiScan[];
  canCreateAudienceDoc: boolean;
  canManageAudienceDocShare: boolean;
  featureEnabled: boolean;
  selectedAudienceDocId?: string;
  scopeArtifactTitlesById: Map<string, string>;
  onOpenCreateModal: () => void;
  onSelectAudienceDoc: (audienceDocId: string) => void;
  onOpenAudienceDoc: (audienceDocId: string) => void;
}) => (
  <section css={SECTION_STYLE}>
    <div css={SECTION_HEADER_ROW_STYLE}>
      <div css={SECTION_COPY_STYLE}>
        <div css={EYEBROW_STYLE}>Audience docs</div>
        <div css={SECTION_TITLE_STYLE}>Shareable copies</div>
        <div css={SECTION_DESCRIPTION_STYLE}>
          Create read-only audience-doc copies from approved or active draft scope versions, then copy a public link when you are ready to share.
        </div>
      </div>
      {canCreateAudienceDoc && (
        <CommonActionButton
          style={PRIMARY_ACTION_BUTTON_STYLE}
          onClick={onOpenCreateModal}
        >
          + New Copy
        </CommonActionButton>
      )}
    </div>
    {audienceDocs.length > 0
      ? (
        <div css={THREE_UP_CARD_GRID_STYLE}>
          {audienceDocs.map(audienceDoc => (
            <AudienceDocArtifactCard
              key={audienceDoc.id}
              audienceDoc={audienceDoc}
              canManageShare={canManageAudienceDocShare}
              featureEnabled={featureEnabled}
              isSelected={audienceDoc.id === selectedAudienceDocId}
              parentArtifactTitle={scopeArtifactTitlesById.get(audienceDoc.parentScanId ?? '')
                ?? 'the selected scope version'}
              onSelect={() => onSelectAudienceDoc(audienceDoc.id)}
              onOpen={() => onOpenAudienceDoc(audienceDoc.id)}
            />
          ))}
        </div>
      )
      : (
        <ArtifactEmptyState
          variant='panel'
          title='No copies yet'
          description='Create a filtered, read-only copy to share with anyone who needs a look.'
        />
      )}
  </section>
);
